import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, UserCheck, UserX } from "lucide-react";

interface UserDetailsDialogProps {
  userId: string | null;
  email?: string;
  name?: string;
  isAdmin?: boolean;
  isExpert?: boolean;
  onToggleAdmin?: () => void | Promise<void>;
  onToggleExpert?: () => void | Promise<void>;
  onAttributeSaved?: (userId: string, attributeName: string, attributeValue: string) => void;
  onDelete?: () => void | Promise<void>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AttributeMap {
  [key: string]: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "object" && error && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

export const UserDetailsDialog = ({
  userId,
  email,
  name,
  isAdmin = false,
  isExpert = false,
  onToggleAdmin,
  onToggleExpert,
  onAttributeSaved,
  onDelete,
  open,
  onOpenChange,
}: UserDetailsDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState<"admin" | "expert" | null>(null);
  const [attributes, setAttributes] = useState<AttributeMap>({});
  const [originalAttributes, setOriginalAttributes] = useState<AttributeMap>({});
  const pendingCloseRef = useRef(false);

  useEffect(() => {
    if (!open || !userId) return;
    pendingCloseRef.current = false;

    let isCurrent = true;

    const fetchAttributes = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("profile_attributes")
        .select("attribute_name, attribute_value")
        .eq("user_id", userId);

      if (!isCurrent) return;

      if (error) {
        toast({ title: "Error", description: "Failed to load attributes", variant: "destructive" });
        setLoading(false);
        return;
      }

      const map: AttributeMap = {};
      (data || []).forEach((row) => {
        map[row.attribute_name] = row.attribute_value;
      });
      setAttributes(map);
      setOriginalAttributes(map);
      setLoading(false);
    };

    fetchAttributes();

    return () => {
      isCurrent = false;
    };
  }, [open, toast, userId]);

  const handleChange = (key: string, value: string) => {
    setAttributes((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleRole = async (role: "admin" | "expert") => {
    setRoleUpdating(role);
    try {
      if (role === "admin") {
        await onToggleAdmin?.();
      } else {
        await onToggleExpert?.();
      }
    } finally {
      setRoleUpdating(null);
    }
  };

  const handleAttributeBlur = async (attribute_name: string) => {
    if (!userId) return;
    const attribute_value = attributes[attribute_name] ?? "";
    const originalValue = originalAttributes[attribute_name] ?? "";
    if (attribute_value === originalValue) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profile_attributes")
        .upsert(
          { user_id: userId, attribute_name, attribute_value },
          { onConflict: "user_id,attribute_name" }
        );
      if (error) throw error;

      setOriginalAttributes((prev) => (
        { ...prev, [attribute_name]: attribute_value }
      ));
      onAttributeSaved?.(userId, attribute_name, attribute_value);
    } catch (error: unknown) {
      setAttributes((prev) => (
        { ...prev, [attribute_name]: originalValue }
      ));
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to save"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      if (pendingCloseRef.current) {
        pendingCloseRef.current = false;
        onOpenChange(false);
      }
    }
  };

  const handleAttributeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.currentTarget.blur();
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete?.();
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (roleUpdating !== null && !nextOpen) return;
    if (saving && !nextOpen) {
      pendingCloseRef.current = true;
      return;
    }
    onOpenChange(nextOpen);
  };

  // Render in a stable, sensible order — known keys first
  const KNOWN_KEYS = ["plan", "credits", "plan_start"];
  const otherKeys = Object.keys(attributes)
    .filter((k) => !KNOWN_KEYS.includes(k))
    .sort();
  const orderedKeys = [...KNOWN_KEYS.filter((k) => k in attributes), ...otherKeys];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
          <DialogDescription>
            {name || "—"} {email ? `(${email})` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2 pb-3 border-b">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleToggleRole("admin")}
                disabled={saving || loading || roleUpdating !== null}
                className="gap-2"
              >
                {roleUpdating === "admin" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserCheck className="w-4 h-4" />
                )}
                {isAdmin ? "Revoke Admin" : "Make Admin"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleToggleRole("expert")}
                disabled={saving || loading || roleUpdating !== null}
                className="gap-2"
              >
                {roleUpdating === "expert" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserCheck className="w-4 h-4" />
                )}
                {isExpert ? "Revoke Expert" : "Make Expert"}
              </Button>
            </div>

            {orderedKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground">No attributes found for this user.</p>
            ) : (
              orderedKeys.map((key) => (
                <div key={key} className="grid grid-cols-3 items-center gap-3">
                  <Label htmlFor={`attr-${key}`} className="text-sm capitalize">
                    {key.replace(/_/g, " ")}
                  </Label>
                  <Input
                    id={`attr-${key}`}
                    value={attributes[key] ?? ""}
                    onChange={(e) => handleChange(key, e.target.value)}
                    onBlur={() => handleAttributeBlur(key)}
                    onKeyDown={handleAttributeKeyDown}
                    disabled={saving || roleUpdating !== null}
                    className="col-span-2"
                  />
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          {onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving || roleUpdating !== null}
              className="gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserX className="h-4 w-4" />
              )}
              Delete user
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={roleUpdating !== null}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
